/**
 * Client for the new `brokers` admin endpoints.
 *
 * Talks to `/api/broker_deals/brokers/...` — the small CRUD surface added
 * in de-hub #185 that backs the Brokers admin page. The `brokers` table
 * lists external land-brokerage firms; each row is referenced by zero or
 * more `deals.brokerage_id`.
 *
 * Endpoints:
 *   - GET    /broker_deals/brokers?include_inactive={bool}
 *   - POST   /broker_deals/brokers
 *   - GET    /broker_deals/brokers/{broker_id}
 *   - PUT    /broker_deals/brokers/{broker_id}        (partial; exclude_unset)
 *   - DELETE /broker_deals/brokers/{broker_id}        (409 if deals reference)
 *
 * Each method takes `{ token, userOid, ... }` and returns the raw axios
 * response — callers destructure `response.data.data` per the standard
 * de-hub envelope ({ success, data, count? }).
 *
 * Mirrors the `dealsService.js` default-export pattern so the page can
 * mock the module wholesale in tests.
 */
import dataService from "./dataservice";
import { clearingAfter, lookupCache } from "./referenceCache";

const BASE = "/broker_deals/brokers";

/**
 * GET /api/broker_deals/brokers?include_inactive={bool}
 *
 * Backend default returns ACTIVE-only; opt in via includeInactive=true to
 * surface deactivated firms (e.g. for the admin "Show inactive" toggle).
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {boolean} [args.includeInactive=false]
 */
const listBrokers = ({ token, userOid, includeInactive = false }) =>
  dataService.request(
    "get",
    BASE,
    token,
    userOid,
    undefined,
    { params: { include_inactive: includeInactive } }
  );

/**
 * GET /api/broker_deals/brokers/{broker_id}
 */
const getBroker = ({ token, userOid, brokerId }) =>
  dataService.getEndpoint(
    `${BASE}/${encodeURIComponent(brokerId)}`,
    token,
    userOid
  );

/**
 * POST /api/broker_deals/brokers
 *
 * Body shape (extra='forbid' on the server):
 *   { company_name (req), vendor_code, contact_name, contact_email,
 *     contact_phone, status ('ACTIVE'|'INACTIVE', default ACTIVE), notes }
 *
 * Server returns 409 with `{detail: "vendor_code '...' already in use"}` if
 * `vendor_code` collides with an existing row.
 */
const createBroker = ({ token, userOid, body }) =>
  clearingAfter(lookupCache, dataService.postEndpoint(BASE, token, userOid, body));

/**
 * PUT /api/broker_deals/brokers/{broker_id}
 *
 * Partial update — server applies `exclude_unset=True`, so only keys
 * present in `body` are touched. Used for both regular edits and the
 * soft-deactivate fallback ({ status: 'INACTIVE' }).
 */
const updateBroker = ({ token, userOid, brokerId, body }) =>
  clearingAfter(
    lookupCache,
    dataService.updateEndpoint(
      `${BASE}/${encodeURIComponent(brokerId)}`,
      token,
      userOid,
      body
    )
  );

/**
 * DELETE /api/broker_deals/brokers/{broker_id}
 *
 * Hard-delete when no deals reference the broker. Server returns 409
 * (with a `detail` count message) if any deals.brokerage_id still points
 * here — the UI catches that and offers a PUT-to-INACTIVE fallback.
 */
const deleteBroker = ({ token, userOid, brokerId }) =>
  clearingAfter(
    lookupCache,
    dataService.deleteEndpoint(
      `${BASE}/${encodeURIComponent(brokerId)}`,
      token,
      userOid
    )
  );

const brokerService = {
  listBrokers,
  getBroker,
  createBroker,
  updateBroker,
  deleteBroker,
};

export default brokerService;
